/**
 * QA override for the buy flow gate.
 *
 * Usage: append `?buyflow=on` to force the buy flow on, or `?buyflow=off`
 * to force the call-center experience on. The choice is persisted in
 * sessionStorage so it survives SPA navigation within the tab.
 *
 * Mapping: `on` -> BUYFLOW_ENABLED='true', `off` -> BUYFLOW_ENABLED='false'.
 *
 * TODO: There is a confusing internal inversion inside
 * src/hooks/order.js (useCallCenterFlag) where the local `buyflow`
 * variable is negated via `!buyflow` inside checkTime(). The end-to-end
 * semantic of BUYFLOW_ENABLED still matches its name (true = buy flow on),
 * but the intermediate logic is hard to read. Clean up that hook so the
 * naming and flow are consistent, then revisit this util if needed.
 */

import { normalizeReferrerUri } from '../helpers/tfn';

const STORAGE_KEY = 'buyflowOverride';
const QUERY_PARAM = 'buyflow';

const REFERRER_STORAGE_KEY = 'buyflowReferrerOverride';
const REFERRER_QUERY_PARAM = 'referrer';

/**
 * Referrers whose visitors always get the buy flow, whatever the call-center
 * clock says. halo.earthlink.net is the Friends & Family landing page and its
 * whole premise is that the visitor self-serves, so it must never be handed the
 * call-in experience (ELNKWEB-2988).
 *
 * Membership is an exact match on the normalized host. `normalizeReferrerUri`
 * lowercases, strips the scheme and cuts at the first `/?#`, so
 * `https://halo.earthlink.net/refer?x=1` matches while
 * `halo.earthlink.net.evil.example.com` and `https://halo.earthlink.net@evil.example.com`
 * do not - the same hardening the redemption allowlist carries.
 */
const ALWAYS_ON_BUYFLOW_REFERRERS = new Set(['halo.earthlink.net']);

/** Exact membership test on a raw referrer value, for callers that already hold one. */
export const isAlwaysOnBuyflowReferrer = (referrerUri) =>
  ALWAYS_ON_BUYFLOW_REFERRERS.has(normalizeReferrerUri(referrerUri || ''));

/**
 * Record that this tab arrived from an always-on referrer.
 *
 * Separate from `readBuyflowReferrerOverride` because the two see different things: that one
 * reads `window.location`, while the caller that matters - `useCallCenterFlag` - resolves its
 * referrer from the router and from redux. A referrer held in redux, or one the router sees
 * that `window.location` no longer does, has to be remembered too, or the exemption dies at
 * the first navigation.
 */
export const rememberBuyflowReferrer = () => {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(REFERRER_STORAGE_KEY, 'on');
};
/** HashRouter puts query params in the hash (e.g. `/#/?buyflow=on`), not in `location.search`. */
export const getLocationSearch = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  if (window.location.search) {
    return window.location.search;
  }
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  return queryIndex >= 0 ? hash.slice(queryIndex) : '';
};

export const readBuyflowOverrideFromQuery = (search) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(search || getLocationSearch());
  const v = params.get(QUERY_PARAM);
  if (v === 'on' || v === 'off') {
    sessionStorage.setItem(STORAGE_KEY, v);
    return v;
  }
  return sessionStorage.getItem(STORAGE_KEY);
};

/**
 * Both query strings, in the order they should be consulted.
 *
 * `getLocationSearch` returns the pre-hash query whenever it is non-empty and
 * only then falls back to the hash query, so a link shaped
 * `?site-token=...#/?referrer=...` would never surface the referrer. The
 * referrer can land either side of the hash, so both get parsed - the same
 * belt-and-braces HomePage already applies to `site-token`.
 */
const getCandidateSearches = () => {
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');

  return [
    window.location.search || '',
    queryIndex >= 0 ? hash.slice(queryIndex) : '',
  ].filter(Boolean);
};

/**
 * `'on'` when this visit came from a referrer that always gets the buy flow,
 * otherwise `null`.
 *
 * The answer is persisted because the referrer only exists on the landing URL:
 * `navigate(routesConfig.products)` carries no query string, so re-reading it
 * per render would go blank on the very page the plan cards live on.
 *
 * Read by `useCallCenterFlag` on every render rather than written once into
 * `config.BUYFLOW_ENABLED` at mount. That earlier shape lost a race in
 * production: `BUYFLOW_ENABLED` is a mutable module value with a public writer
 * (`window._elnkCheckout.setElnkConfig`), and by the time the plan cards
 * rendered the mount-time write no longer held. Evaluating per render is what
 * the `invite.earthlink.net` exemption has always done, and it cannot be
 * clobbered by a later config write.
 *
 * Writing on read is deliberate and idempotent: the landing render is the only
 * moment the referrer is observable, and the header calls this hook there.
 */
export const readBuyflowReferrerOverride = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const referrerUri = getCandidateSearches()
    .map((search) => new URLSearchParams(search).get(REFERRER_QUERY_PARAM))
    .find(Boolean) || '';

  if (ALWAYS_ON_BUYFLOW_REFERRERS.has(normalizeReferrerUri(referrerUri))) {
    sessionStorage.setItem(REFERRER_STORAGE_KEY, 'on');
    return 'on';
  }

  return sessionStorage.getItem(REFERRER_STORAGE_KEY);
};

export const buyflowOverrideToConfigValue = (override) => {
  if (override === 'on') {
    return 'true';
  }
  if (override === 'off') {
    return 'false';
  }
  return null;
};
